declare global {
  interface Window {
    __RUNTIME_CONFIG__?: {
      REACT_APP_API_URL?: string;
      REACT_APP_PAGINATION_SIZE?: string;
      REACT_APP_ROUTE_PREFIX?: string;
      REACT_APP_SUBTITLES_VISIBLES?: string;
      REACT_APP_URL_WHATSAPP?: string;
      REACT_APP_URL_TELEGRAM?: string;
      REACT_APP_URL_LINKEDIN_JOSE?: string;
      REACT_APP_URL_LINKEDIN_DANIEL?: string;
      REACT_APP_URL_LINKEDIN_ALEX?: string;
      REACT_APP_CONTACT_URL?: string;
    };
  }
}

const DEFAULT_PAGINATION_SIZE = 10;
const DEFAULT_ROUTE_PREFIX = '/admin';

function stripTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

function isLoopbackHost(hostname: string): boolean {
  return (
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]'
  );
}

function isLoopbackApiUrl(url: string): boolean {
  if (!url) {
    return false;
  }
  try {
    return isLoopbackHost(new URL(url).hostname);
  } catch {
    return false;
  }
}

function isInternalServiceHost(hostname: string): boolean {
  if (isLoopbackHost(hostname)) {
    return false;
  }
  return !hostname.includes('.');
}

function isInternalServiceApiUrl(url: string): boolean {
  if (!url) {
    return false;
  }
  try {
    return isInternalServiceHost(new URL(url).hostname);
  } catch {
    return false;
  }
}

function isBrowserOnLoopback(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return isLoopbackHost(window.location.hostname);
}

function resolveApiBaseUrl(raw: string | undefined): string {
  const trimmed = stripTrailingSlash(String(raw ?? '').trim());
  if (!trimmed) {
    return '';
  }
  if (!isBrowserOnLoopback()) {
    if (isLoopbackApiUrl(trimmed) || isInternalServiceApiUrl(trimmed)) {
      return '';
    }
  }
  return trimmed;
}

function getRuntimeString(
  key: keyof NonNullable<Window['__RUNTIME_CONFIG__']>,
  fallback = ''
): string {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    const cfg = window.__RUNTIME_CONFIG__;
    if (Object.prototype.hasOwnProperty.call(cfg, key)) {
      return String(cfg[key] ?? '').trim();
    }
  }
  const fromEnv = process.env[key]?.trim();
  return fromEnv ?? fallback;
}

export function getApiBaseUrl(): string {
  return resolveApiBaseUrl(getRuntimeString('REACT_APP_API_URL'));
}

export function getPaginationSize(): number {
  const parsed = Number.parseInt(
    getRuntimeString('REACT_APP_PAGINATION_SIZE'),
    10
  );
  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }
  return DEFAULT_PAGINATION_SIZE;
}

export function getDefaultRoutePrefix(): string {
  const prefix = getRuntimeString(
    'REACT_APP_ROUTE_PREFIX',
    DEFAULT_ROUTE_PREFIX
  );
  if (!prefix.startsWith('/')) {
    return `/${prefix}`;
  }
  return prefix;
}

export function getSubtitlesVisible(): boolean {
  return (
    getRuntimeString('REACT_APP_SUBTITLES_VISIBLES').toLowerCase() === 'true'
  );
}

export function getWhatsappUrl(): string {
  return getRuntimeString('REACT_APP_URL_WHATSAPP');
}

export function getTelegramUrl(): string {
  return getRuntimeString('REACT_APP_URL_TELEGRAM');
}

export function getLinkedInJoseUrl(): string {
  return getRuntimeString('REACT_APP_URL_LINKEDIN_JOSE');
}

export function getLinkedInDanielUrl(): string {
  return getRuntimeString('REACT_APP_URL_LINKEDIN_DANIEL');
}

export function getLinkedInAlexUrl(): string {
  return getRuntimeString('REACT_APP_URL_LINKEDIN_ALEX');
}

export function getContactEmail(): string {
  return getRuntimeString('REACT_APP_CONTACT_URL');
}
